import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleCheeseGuide() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "buy-good-cheese-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "shops-overview": "Top Cheese Shops in Vancouver",
    "buying-tips": "What to Look for When Buying Cheese",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://buycheese.com/",
      label: "les amis du FROMAGE - Official Website",
    },
    {
      url: "https://bentonscheese.com/",
      label: "Benton Brothers Fine Cheese - Official Site",
    },
    {
      url: "https://granvilleisland.com/",
      label: "Granville Island Public Market – Directory & Map",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each ID must match a reference in the text.
  // Coordinates are from the conversation (approx lat/lng with 5 decimals).
  const locations = [
    {
      id: "shop-la-amis-du-fromage-kits",
      lat: 49.27050,
      lng: -123.14050,
      popupText: "les amis du FROMAGE (Kitsilano location)",
    },
    {
      id: "shop-la-amis-du-fromage-hastings",
      lat: 49.28120,
      lng: -123.08490,
      popupText: "les amis du FROMAGE (East Hastings location)",
    },
    {
      id: "shop-benton-granville",
      lat: 49.27116,
      lng: -123.13378,
      popupText: "Benton Brothers Fine Cheese (Granville Island)",
    },
    {
      id: "shop-benton-kerrisdale",
      lat: 49.23463,
      lng: -123.15871,
      popupText: "Benton Brothers Fine Cheese (Kerrisdale)",
    },
    {
      id: "shop-la-grotta",
      lat: 49.27200,
      lng: -123.06930,
      popupText: "La Grotta del Formaggio (Commercial Drive)",
    },
    {
      id: "shop-dussas",
      lat: 49.27116,
      lng: -123.13378,
      popupText: "Dussa’s Ham & Cheese Ltd. (Granville Island)",
    },
    {
      id: "shop-bosa-victoria",
      lat: 49.28150,
      lng: -123.05500,
      popupText: "Bosa Foods (Victoria Drive)",
    },
    {
      id: "shop-bosa-kootenay",
      lat: 49.27670,
      lng: -123.02350,
      popupText: "Bosa Foods (Kootenay St)",
    },
    {
      id: "shop-cheese-man",
      lat: 49.32045,
      lng: -123.10365,
      popupText: "The Cheese Man (North Vancouver)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Where to Buy Good Cheese in Vancouver: A Complete Guide</title>
        <meta
          name="description"
          content="Discover Vancouver's top cheese shops, tips for choosing high-quality cheese, local regulations, and FAQs in our comprehensive cheese guide."
        />
        <meta
          name="keywords"
          content="cheese, Vancouver, cheese shops, artisanal cheese, local cheese regulations"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/buy-good-cheese-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Where to Buy Good Cheese in Vancouver: A Complete Guide"
        />
        <meta
          property="og:description"
          content="Discover Vancouver's top cheese shops, tips for choosing high-quality cheese, local regulations, and FAQs in our comprehensive cheese guide."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/buy-good-cheese-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/buy-good-cheese-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Where to Buy Good Cheese in Vancouver: A Complete Guide"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver's top cheese shops, tips for choosing high-quality cheese, local regulations, and FAQs in our comprehensive cheese guide."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/buy-good-cheese-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/buy-good-cheese-vancouver.jpg"
              }
              alt="A selection of cheeses in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a paradise for cheese lovers, offering specialty
              shops and gourmet delis with everything from local artisan gouda
              to imported French brie. Whether you’re seeking a perfectly aged
              cheddar or a creamy local chèvre, this guide will help you
              discover <strong>where</strong> to buy top-quality cheese in the
              city and <strong>how</strong> to select and store it.
            </p>
            <p>
              Below, you’ll find a detailed breakdown of Vancouver’s best cheese
              shops (with coordinates for easy navigation), tips for identifying
              the highest-quality cheeses, local regulations about raw milk
              cheese, and a helpful FAQ section to address common questions.
            </p>

            {/* SECTION: Top Cheese Shops in Vancouver */}
            <h2 id="shops-overview">Top Cheese Shops in Vancouver</h2>
            <p>
              Vancouver’s cheese scene is diverse, influenced by its rich
              culinary culture. You’ll find shops specializing in Canadian
              farmstead cheeses, European classics, and more. Each offers a
              unique experience, from bustling markets to family-run boutiques.
            </p>

            <h3>1. les amis du FROMAGE</h3>
            <p>
              <strong>Kitsilano Location</strong> (
              <span
                ref={(el) => setSpotRef("shop-la-amis-du-fromage-kits", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.27050}
                  coordX={-123.14050}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              ) – 1752 West 2nd Ave, Vancouver, BC.
              <br />
              <strong>East Hastings Location</strong> (
              <span
                ref={(el) =>
                  setSpotRef("shop-la-amis-du-fromage-hastings", el)
                }
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.28120}
                  coordX={-123.08490}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              ) – 843 East Hastings St, Vancouver, BC.
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Daily 10:00 AM – 6:00 PM (closed holiday
                Mondays).
              </li>
              <li>
                <strong>Highlights:</strong> An award-winning, family-run shop
                offering a massive variety of cheeses—both local and imported.
                They encourage you to taste before you buy, with helpful
                cheesemongers to guide your selection.
              </li>
              <li>
                <strong>Price Range:</strong> $$–$$$ (most cheeses about
                $6–$15/100 g, depending on rarity).
              </li>
            </ul>

            <h3>2. Benton Brothers Fine Cheese</h3>
            <p>
              <strong>Granville Island Public Market</strong> (
              <span
                ref={(el) => setSpotRef("shop-benton-granville", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.27116}
                  coordX={-123.13378}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              ) – 1689 Johnston St, Vancouver, BC.
              <br />
              <strong>Kerrisdale</strong> (
              <span
                ref={(el) => setSpotRef("shop-benton-kerrisdale", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.23463}
                  coordX={-123.15871}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              ) – 2104 West 41st Ave, Vancouver, BC.
            </p>
            <ul>
              <li>
                <strong>Hours (Granville Island):</strong> Daily 9:00 AM – 7:00
                PM.
              </li>
              <li>
                <strong>Hours (Kerrisdale):</strong> Tue–Sat 10:00 AM – 6:00 PM;
                Sun 12:00 PM – 5:00 PM; closed Mon.
              </li>
              <li>
                <strong>Highlights:</strong> A family-owned shop emphasizing
                artisanal, small-production cheeses. The staff happily provide
                samples and pairing recommendations, with a rotating selection
                of new imports and local varieties.
              </li>
              <li>
                <strong>Price Range:</strong> $$–$$$.
              </li>
            </ul>

            <h3>3. La Grotta del Formaggio</h3>
            <p>
              <strong>Commercial Drive</strong> (
              <span
                ref={(el) => setSpotRef("shop-la-grotta", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.27200}
                  coordX={-123.06930}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              ) – 1791 Commercial Drive, Vancouver, BC.
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Mon–Thu & Sat: 9:00 AM – 6:00 PM; Fri:
                9:00 AM – 7:00 PM; Sun: 10:00 AM – 6:00 PM.
              </li>
              <li>
                <strong>Highlights:</strong> An old-school Italian deli with an
                impressive assortment of Italian cheeses and deli items. Known
                for house-made sandwiches and friendly, bustling atmosphere.
              </li>
              <li>
                <strong>Price Range:</strong> $–$$.
              </li>
            </ul>

            <h3>4. Dussa’s Ham & Cheese Ltd.</h3>
            <p>
              <strong>Granville Island Public Market</strong> (
              <span
                ref={(el) => setSpotRef("shop-dussas", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.27116}
                  coordX={-123.13378}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              ) – 1669 Johnston St, Vancouver, BC.
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Daily, roughly 9:00 AM – 6:00 PM.
              </li>
              <li>
                <strong>Highlights:</strong> A family-run stall known for its
                curated range of European cheeses and charcuterie. Friendly
                service and a small, cozy space inside the market.
              </li>
              <li>
                <strong>Price Range:</strong> $$.
              </li>
            </ul>

            <h3>5. Bosa Foods</h3>
            <p>
              <strong>Victoria Drive</strong> (
              <span
                ref={(el) => setSpotRef("shop-bosa-victoria", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.28150}
                  coordX={-123.05500}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              ) – 562 Victoria Dr, Vancouver, BC.
              <br />
              <strong>Kootenay St</strong> (
              <span
                ref={(el) => setSpotRef("shop-bosa-kootenay", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.27670}
                  coordX={-123.02350}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              ) – 1465 Kootenay St, Vancouver, BC.
            </p>
            <ul>
              <li>
                <strong>Hours (Victoria Dr):</strong> Mon–Fri 8:30 AM – 6:00 PM,
                Sat 8:00 AM – 5:30 PM, Sun 10:00 AM – 4:00 PM.
              </li>
              <li>
                <strong>Hours (Kootenay St):</strong> Mon–Sat 8:00 AM – 5:30 PM,
                closed Sun.
              </li>
              <li>
                <strong>Highlights:</strong> A long-standing Italian grocery
                offering a huge cheese counter and competitive prices. Specialize
                in Italian imports: Parmigiano Reggiano, buffalo mozzarella,
                burrata, and more.
              </li>
              <li>
                <strong>Price Range:</strong> $–$$.
              </li>
            </ul>

            <h3>6. The Cheese Man</h3>
            <p>
              <strong>North Vancouver</strong> (
              <span
                ref={(el) => setSpotRef("shop-cheese-man", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.32045}
                  coordX={-123.10365}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              ) – 991 West 3rd St, North Vancouver, BC.
            </p>
            <ul>
              <li>
                <strong>Hours:</strong> Tue–Sat 10:30 AM – 5:30 PM; closed
                Sunday & Monday.
              </li>
              <li>
                <strong>Highlights:</strong> A hidden gem operating since 1978,
                offering international cheeses from English cheddar to Dutch
                gouda and local BC specialties. Friendly, old-school vibe with
                good prices.
              </li>
              <li>
                <strong>Price Range:</strong> $$.
              </li>
            </ul>

            {/* SECTION: What to Look for When Buying Cheese */}
            <h2 id="buying-tips">What to Look for When Buying Cheese</h2>
            <p>
              Choosing the right cheese involves more than just a quick glance.
              Here are a few tips:
            </p>
            <ul>
              <li>
                <strong>Fresh vs. Aged:</strong> Fresh cheeses (like feta,
                mozzarella) should appear moist but not slimy, while aged cheeses
                (like cheddar, Parmesan) should have no large cracks or dry
                edges.
              </li>
              <li>
                <strong>Check Quality Indicators:</strong> A quick smell test
                helps; cheese shouldn’t smell “off.” With bloomy-rind cheeses
                like brie, white mold is expected, but avoid discolored or
                ammonia-scented areas.
              </li>
              <li>
                <strong>Ask the Experts:</strong> Most shops offer samples. Tell
                them your flavor preferences, and they can direct you to
                something perfect for your palate.
              </li>
              <li>
                <strong>Pairing Suggestions:</strong> Fruits, nuts, and honey
                complement many cheeses. Pair bold cheeses with robust wines,
                milder cheeses with lighter beverages.
              </li>
            </ul>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              In Canada, raw (unpasteurized) milk cheeses are allowed if they’ve
              been aged at least 60 days. Soft, unaged raw milk cheeses under 60
              days aren’t sold here. Pregnant women or those with weakened
              immunity should consult a healthcare provider before consuming
              raw-milk cheese. 
            </p>
            <p>
              <strong>Storage Tips:</strong> Wrap cheese in wax or cheese paper,
              then place in a loose plastic bag. Let cheese come to room
              temperature before serving for the best flavor. Keep strong
              cheeses separate to prevent flavor crossover.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Where can I find lactose-free cheese in Vancouver?</h3>
              <p>
                Aged cheeses (cheddar, Parmesan, Swiss) are naturally low in
                lactose. Many shops also carry enzyme-treated lactose-free
                cheese or vegan alternatives. Ask the cheesemonger for details.
              </p>

              <h3>2. Do these shops offer delivery or online ordering?</h3>
              <p>
                Several, like les amis du FROMAGE, provide online ordering and
                local delivery. Others may allow phone-in orders with curbside
                pickup. Check each store’s website for details.
              </p>

              <h3>3. Are raw milk cheeses safe?</h3>
              <p>
                Yes. By Canadian law, raw milk cheeses sold here are aged at
                least 60 days, which helps eliminate harmful bacteria. However,
                pregnant individuals or those with compromised immunity may want
                pasteurized options.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s cheese scene is both diverse and accessible. With
              these top shops, practical tips, and a little help from local
              cheesemongers, you’re bound to find the perfect cheese for any
              occasion. From classic imports to innovative local creations,
              there’s something to delight every palate. Enjoy exploring –
              remember to store your cheese properly and serve at room
              temperature for the best flavor!
            </p>
            <p>
              <em>
                Disclaimer: Store hours and offerings may change; always check
                official resources for updates.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}